import { FC, memo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useRouteMatch } from "react-router-dom";
import { Card, CardSizesEnum } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { convertNumberToCurrency } from "@monorepo/helpers";
import theme from "@monorepo/styles/theme";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Typography } from "@mui/material";
import { Box, styled, SxProps } from "@mui/system";
import { format } from "date-fns";
import { reverse } from "named-urls";

interface IHistoryCardProps {
  title: string;
  subtitle?: string;
  sum: string;
  isActive?: boolean;
  icon: ReactNode;
  sx?: SxProps;
}

interface IProps {
  date: Date;
  activePeriod?: string;
  cartGroupId: string;
  monthData: {
    entriesCount: number;
    totalSum: number;
  };
  sx?: SxProps;
}

const StyledLink = styled(Link)(() => ({
  textDecoration: "none",
  color: "inherit",
}));

const HistoryCard: FC<IHistoryCardProps> = ({ title, subtitle, sum, isActive, icon, sx }) => {
  return (
    <Card active={isActive} size={CardSizesEnum.SMALL} sx={sx}>
      <Box
        display="grid"
        gridTemplateColumns="minmax(2.5rem, 1fr) 10fr 2fr"
        alignItems="center"
        sx={{
          gridGap: theme.spacing(2),
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center">
          {icon}
        </Box>
        <Box display="flex" flexDirection="column">
          <Typography>{title}</Typography>
          <Typography color={theme.palette.text.disabled} fontWeight="0.825rem">
            {subtitle}
          </Typography>
        </Box>
        <Typography whiteSpace="nowrap">{sum}</Typography>
      </Box>
    </Card>
  );
};

export const HistoryMonthSummaryCard: FC<IProps> = memo(({ date, activePeriod, cartGroupId, monthData, sx }) => {
  const { t } = useTranslation();

  const entryMonth = String(date.getMonth() + 1);
  const isInSummary = !!useRouteMatch(routeConfig.dashboard.payments.history.cartGroup.summary.self);

  const isActive = isInSummary && String(activePeriod) === format(date, "yyyy-MM");

  return (
    <>
      {date && (
        <StyledLink
          to={reverse(routeConfig.dashboard.payments.history.cartGroup.summary.self, {
            period: format(date, "yyyy-MM"),
            cartGroupId,
          })}
        >
          <HistoryCard
            isActive={isActive}
            title={t(`months_summary.${Number(entryMonth)}`)}
            subtitle={t("payments.payments", { count: monthData?.entriesCount })}
            icon={<DescriptionOutlinedIcon />}
            sum={convertNumberToCurrency(monthData?.totalSum)}
            sx={sx}
          />
        </StyledLink>
      )}
    </>
  );
});
HistoryMonthSummaryCard.displayName = "HistoryMonthSummaryCard";
